import * as React from 'react';
import type {
  ControlComponent,
  EntityFormControlProps,
} from '@web-config-app/entity-form';
import {
  EntityFormControl,
  AddItemModalTriggers,
  AddEntityReferenceFromListModal,
} from '@web-config-app/entity-form';
import { EntityLoader } from '@web-config-app/core-react';
import type {
  AssetType,
  DomainEntity,
  EntityDetail,
} from '@web-config-app/core';
import { ASSET_TYPES, ASSET_TYPE_TO_ENTITY_TYPE } from '@web-config-app/core';
import { CopyButton, LeadingThumbnail } from '@web-config-app/core-react-ui';
import {
  Box,
  Button,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useEntityReferenceControlState } from '../../controls-complex/entity-select-control/internals/use-entity-reference-control-state';
import { EntitySelectModalHeading } from '../../controls-complex/entity-select-control/internals/entity-select-modal-heading.component';
import { getAssetUrlFromDomainEntityAttributes } from '../../../utils/get-asset-fields-from-domain-entity-attributes/get-asset-fields-from-domain-entity-attributes';

const assetTypes: readonly string[] = ASSET_TYPES;

const isAssetType = (value: unknown): value is AssetType =>
  typeof value === 'string' && assetTypes.includes(value);

interface AssetUriControlContentsProps extends EntityFormControlProps {
  referencedEntityConfig: DomainEntity;
  assetType: AssetType;
}

const AssetUriControlContents: React.FC<AssetUriControlContentsProps> = ({
  data,
  handleChange,
  path,
  enabled,
  referencedEntityConfig,
  assetType,
}) => {
  const { formatMessage } = useIntl();

  const onClear = () => handleChange(path, undefined);

  const {
    closeChooseFromList,
    openChooseFromList,
    isAddingFromList,
    addItemTriggerList,
  } = useEntityReferenceControlState({ domainEntity: referencedEntityConfig });

  const onSubmitChooseFromList = (entityDetail: EntityDetail) => {
    const url = getAssetUrlFromDomainEntityAttributes(
      entityDetail?.attributes?.domainEntityAttributes,
    );
    if (!url) throw new Error('Selected entity does not expose a URL');

    handleChange(path, url);
    closeChooseFromList();
  };

  return (
    <>
      <Box css={{ width: '100%' }}>
        {data ? (
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="spaceBetween"
            verticalAlignment="center"
            spacing="$half"
          >
            <StackLayout
              orientation="horizontal"
              spacing="$half"
              verticalAlignment="center"
              css={{ width: 0 }}
            >
              <LeadingThumbnail assetType={assetType} url={data} />
              <>
                <UtilityText
                  emphasis="emphasized"
                  css={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    flexShrink: 1,
                    maxWidth: '25rem',
                  }}
                >
                  {data}
                </UtilityText>
                <CopyButton
                  label={formatMessage({ id: 'COPY' }, { title: 'URL' })}
                  priority="tertiary"
                  contents={data}
                  hideLabel
                />
              </>
            </StackLayout>
            <StackLayout
              orientation="horizontal"
              spacing="$one"
              verticalAlignment="center"
              css={{ flexGrow: 0 }}
            >
              <Button
                size="medium"
                priority="secondary"
                hideLabel
                quiet
                onClick={openChooseFromList}
                icon="interfaceChange"
                disabled={!enabled}
              >
                {formatMessage({ id: 'CHANGE_SELECTION' })}
              </Button>
              <Button
                type="button"
                icon="tinyClear"
                hideLabel
                quiet
                priority="tertiary"
                size="toolbar"
                disabled={!enabled}
                onClick={onClear}
              >
                {formatMessage({ id: 'CLEAR_SELECTED_ASSET' })}
              </Button>
            </StackLayout>
          </StackLayout>
        ) : (
          <AddItemModalTriggers
            triggerList={addItemTriggerList}
            disabled={!enabled}
          />
        )}
      </Box>

      <AddEntityReferenceFromListModal
        headingLabel={
          <EntitySelectModalHeading domainEntity={referencedEntityConfig} />
        }
        open={isAddingFromList}
        onCancel={closeChooseFromList}
        domainEntity={referencedEntityConfig}
        onSubmit={onSubmitChooseFromList}
        isRelationshipReference={false}
      />
    </>
  );
};

export const AssetUriControl: ControlComponent = (props) => (
  <EntityFormControl
    {...props}
    renderControl={(controlProps) => {
      const { schema } = controlProps;
      const rawAssetType = schema['x-entity-asset']?.type;
      const assetType = isAssetType(rawAssetType) ? rawAssetType : undefined;
      const entityType = assetType
        ? ASSET_TYPE_TO_ENTITY_TYPE[assetType]
        : undefined;

      if (!assetType || !entityType) {
        throw new Error('Asset type is required for asset uri control');
      }

      return (
        <EntityLoader
          entityType={entityType}
          render={(domainEntityConfig) => (
            <AssetUriControlContents
              {...controlProps}
              referencedEntityConfig={domainEntityConfig}
              assetType={assetType}
            />
          )}
        />
      );
    }}
  />
);
